import { Commodity, Unit } from '@enmon/commons';
import {
  ElectricityDeviceCategory,
  ElectricityMeterMeasurementType,
  GasDeviceCategory,
  ThermalMeasurementType,
  InstallationPosition,
  Mounting,
  Orientation,
  OtherDeviceCategory,
  TubeMaterial,
  WaterMeterMeasurementType,
} from '@enmon/commons/lib/shared/meter';
import { InventoryMeterMountType } from '@enmon/commons/lib/tools';
import { MeterAccessibility, MeterOwnerType } from '@enmon/commons/lib/enmon';
import { COMMODITY_CONFIG } from 'consts/commodities';
import {
  ElectricityDeviceCategory as electricityDeviceCategoryMessages,
  ElectricityMeasurementType as electricityMeasurementTypeMessages,
  GasDeviceCategory as gasDeviceCategoryMessages,
  InstallationPosition as installationPositionMessages,
  MeterAccessibility as meterAccessibilityMessages,
  InventoryMeterMountType as inventoryMeterMountTypeMessages,
  MeterOwnerType as meterOwnerTypeMessages,
  Mounting as mountingMessages,
  Orientation as orientationMessages,
  ThermalMeasurementType as thermalMeasurementTypeMessages,
  OtherDeviceCategory as otherDeviceCategoryMessages,
  TubeMaterial as tubeMaterialMessages,
  WaterMeasurementType as waterMeterMeasurementTypeMessages,
} from './messages';

export const deviceCategoryTranslated = {
  [OtherDeviceCategory.Gateway]: otherDeviceCategoryMessages.gateway,
  [OtherDeviceCategory.LoRaGateway]: otherDeviceCategoryMessages.loRaGateway,
  [OtherDeviceCategory.LoRaGatewayPowerSupply]: otherDeviceCategoryMessages.loRaGatewayPowerSupply,
  [OtherDeviceCategory.Thermometer]: otherDeviceCategoryMessages.thermometer,
  [ElectricityDeviceCategory.Basic]: electricityDeviceCategoryMessages.basic,
  [ElectricityDeviceCategory.FourQuadrants]: electricityDeviceCategoryMessages.fourQuadrants,
  [ElectricityDeviceCategory.Multimeter]: electricityDeviceCategoryMessages.multimeter,
  [ElectricityDeviceCategory.SmartCircuitBreaker]: electricityDeviceCategoryMessages.smartCircuitBreaker,
  [GasDeviceCategory.Rotary]: gasDeviceCategoryMessages.rotary,
  [GasDeviceCategory.Membrane]: gasDeviceCategoryMessages.membrane,
  [GasDeviceCategory.Turbine]: gasDeviceCategoryMessages.turbine,
  [GasDeviceCategory.Quantometer]: gasDeviceCategoryMessages.quantometer,
};

export const electricityMeterMeasurementTypeTranslated = {
  [ElectricityMeterMeasurementType.Inductive]: electricityMeasurementTypeMessages.inductive,
  [ElectricityMeterMeasurementType.Static]: electricityMeasurementTypeMessages.static,
};

export const waterMeterMeasurementTypeTranslated = {
  [WaterMeterMeasurementType.Mechanical]: waterMeterMeasurementTypeMessages.mechanical,
  [WaterMeterMeasurementType.Static]: waterMeterMeasurementTypeMessages.static,
};

export const mountingTranslated = {
  [Mounting.DIN]: mountingMessages.din,
  [Mounting.Cross]: mountingMessages.cross,
  [Mounting.Panel]: mountingMessages.panel,
};

export const accessibilityTranslated = {
  [MeterAccessibility.Good]: meterAccessibilityMessages.good,
  [MeterAccessibility.Tenant]: meterAccessibilityMessages.tenant,
  [MeterAccessibility.Basement]: meterAccessibilityMessages.basement,
  [MeterAccessibility.Ceiling]: meterAccessibilityMessages.ceiling,
  [MeterAccessibility.Shaft]: meterAccessibilityMessages.shaft,
  [MeterAccessibility.FloodedShaft]: meterAccessibilityMessages.floodedShaft,
  [MeterAccessibility.High]: meterAccessibilityMessages.high,
  [MeterAccessibility.FairlyHigh]: meterAccessibilityMessages.fairlyHigh,
  [MeterAccessibility.VeryHigh]: meterAccessibilityMessages.veryHigh,
  [MeterAccessibility.Bad]: meterAccessibilityMessages.bad,
};

export const ownerTypeTranslated = {
  [MeterOwnerType.Client]: meterOwnerTypeMessages.client,
  [MeterOwnerType.Tenant]: meterOwnerTypeMessages.tenant,
  [MeterOwnerType.UtilityCompany]: meterOwnerTypeMessages.utilityCompany,
  [MeterOwnerType.Lessor]: meterOwnerTypeMessages.lessor,
};

export const tubeMaterialTranslated = {
  [TubeMaterial.Copper]: tubeMaterialMessages.copper,
  [TubeMaterial.Plastic]: tubeMaterialMessages.plastic,
  [TubeMaterial.Steel]: tubeMaterialMessages.steel,
};

export const installationPositionTranslated = {
  [InstallationPosition.Forward]: installationPositionMessages.forward,
  [InstallationPosition.Return]: installationPositionMessages.return,
};

export const meterMountTypeTranslated = {
  [InventoryMeterMountType.Free]: inventoryMeterMountTypeMessages.free,
  [InventoryMeterMountType.MountedFree]: inventoryMeterMountTypeMessages.mountedFree,
  [InventoryMeterMountType.MountedOccupied]: inventoryMeterMountTypeMessages.mountedOccupied,
};

export const commodityTranslated = {
  [Commodity.Electricity]: COMMODITY_CONFIG[Commodity.Electricity].message,
  [Commodity.Water]: COMMODITY_CONFIG[Commodity.Water].message,
  [Commodity.NaturalGas]: COMMODITY_CONFIG[Commodity.NaturalGas].message,
  [Commodity.Thermal]: COMMODITY_CONFIG[Commodity.Thermal].message,
  [Commodity.Other]: COMMODITY_CONFIG[Commodity.Other].message,
};

export const commodityDefaultUnit = {
  [Commodity.Electricity]: Unit.kWh,
  [Commodity.Water]: Unit.m3,
  [Commodity.NaturalGas]: Unit.m3,
  [Commodity.Thermal]: Unit.GJ,
  [Commodity.Other]: Unit.GJ,
};

export const orientationTranslated = {
  [Orientation.Horizontal]: orientationMessages.horizontal,
  [Orientation.VerticalFlowUp]: orientationMessages.verticalFlowUp,
  [Orientation.VerticalFlowDown]: orientationMessages.verticalFlowDown,
};

export const thermalMeasurementTypeTranslated = {
  [ThermalMeasurementType.Heat]: thermalMeasurementTypeMessages.heat,
  [ThermalMeasurementType.Cold]: thermalMeasurementTypeMessages.cold,
  [ThermalMeasurementType.Both]: thermalMeasurementTypeMessages.both,
};

export enum FlowrateQp {
  'Qp-0.6' = 0.6,
  'Qp-1.5' = 1.5,
  'Qp-2.5' = 2.5,
  'Qp-3.5' = 3.5,
  'Qp-6' = 6,
  'Qp-10' = 10,
  'Qp-15' = 15,
  'Qp-25' = 25,
  'Qp-30' = 30,
  'Qp-40' = 40,
  'Qp-60' = 60,
  'Qp-100' = 100,
  'Qp-150' = 150,
  'Qp-250' = 250,
}

export enum FlowrateQ3 {
  'Q3-1.6' = 1.6,
  'Q3-2.5' = 2.5,
  'Q3-4' = 4,
  'Q3-6.3' = 6.3,
  'Q3-10' = 10,
  'Q3-16' = 16,
  'Q3-25' = 25,
  'Q3-40' = 40,
  'Q3-63' = 63,
  'Q3-100' = 100,
  'Q3-160' = 160,
  'Q3-260' = 260,
  'Q3-400' = 400,
  'Q3-630' = 630,
  'Q3-1000' = 1000,
}

export enum FlowrateQn {
  'Qn-1.6' = 1.6,
  'Qn-2.5' = 2.5,
  'Qn-3.5' = 3.5,
  'Qn-6' = 6,
  'Qn-10' = 10,
  'Qn-15' = 15,
  'Qn-25' = 25,
  'Qn-40' = 40,
  'Qn-60' = 60,
  'Qn-100' = 100,
  'Qn-150' = 150,
  'Qn-200' = 200,
}
